import ACARASchool from "@/models/APIModels/ACARASchool";
import { getApiClient, IApiClient } from "./ApiClient/ApiClient";
import SchoolDetail from "@/models/APIModels/SchoolDetail";
import SchoolList from "@/models/APIModels/SchoolList";
import SchoolBankAccount from "@/models/APIModels/SchoolBankAccount";

import logger, { LoggingSource } from "@/shared/Logger";
import IResponseModel from "./ResponseModel";

export default class SchoolApi {
  private readonly _apiClient: IApiClient;

  private readonly URL = "/school/";

  constructor(apiClient: IApiClient = getApiClient()) {
    this._apiClient = apiClient;
  }

  async getSchoolByReference(
    reference: string,
  ): Promise<IResponseModel<SchoolDetail>> {
    try {
      logger.info("getSchoolByReference...", LoggingSource.UISchool);

      logger.debug(`Parameter Reference: ${reference}`, LoggingSource.UISchool);

      logger.timeStart("getSchoolByReference call", LoggingSource.UISchool);
      const result = await this._apiClient.get<SchoolDetail>(
        "/school/" + reference,
      );

      logger.timeEnd("getSchoolByReference call", LoggingSource.UISchool);

      logger.debug(
        `getSchoolByReference response: ${JSON.stringify(result)}`,
        LoggingSource.UISchool,
      );

      return result;
    } catch (error) {
      logger.timeEnd("getSchoolByReference call", LoggingSource.UISchool);
      logger.error(JSON.stringify(error), LoggingSource.UISchool);

      throw error;
    } finally {
      logger.info("...getSchoolByReference", LoggingSource.UISchool);
    }
  }

  async getSchoolByAcaraId(
    acaraId: number,
    schoolStatusCds?: string,
  ): Promise<IResponseModel<SchoolDetail>> {
    try {
      logger.info("getSchoolByAcaraId...", LoggingSource.UISchool);

      logger.debug(`Parameter ACARAId: ${acaraId}`, LoggingSource.UISchool);

      logger.timeStart("getSchoolByAcaraId call", LoggingSource.UISchool);
      const result = await this._apiClient.get<SchoolDetail>(
        `/school/byacaraid/${acaraId}` +
          (schoolStatusCds
            ? `?schoolStatusCds=${encodeURIComponent(schoolStatusCds)}`
            : ""),
      );

      logger.timeEnd("getSchoolByAcaraId call", LoggingSource.UISchool);

      logger.debug(
        `getSchoolByAcaraId response: ${JSON.stringify(result)}`,
        LoggingSource.UISchool,
      );

      return result;
    } catch (error) {
      logger.timeEnd("getSchoolByAcaraId call", LoggingSource.UISchool);
      logger.error(JSON.stringify(error), LoggingSource.UISchool);

      throw error;
    } finally {
      logger.info("...getSchoolByAcaraId", LoggingSource.UISchool);
    }
  }

  async getSchoolBankAccount(
    reference: string,
  ): Promise<IResponseModel<SchoolBankAccount>> {
    try {
      logger.info("getSchoolBankAccount...", LoggingSource.UISchool);

      logger.debug(`Parameter Reference: ${reference}`, LoggingSource.UISchool);

      logger.timeStart("getSchoolBankAccount call", LoggingSource.UISchool);
      const result = await this._apiClient.get<SchoolBankAccount>(
        this.URL + `${reference}/bankaccount`,
      );

      logger.timeEnd("getSchoolBankAccount call", LoggingSource.UISchool);

      logger.debug(
        `getSchoolBankAccount response: ${JSON.stringify(result)}`,
        LoggingSource.UISchool,
      );

      return result;
    } catch (error) {
      logger.timeEnd("getSchoolBankAccount call", LoggingSource.UISchool);
      logger.error(JSON.stringify(error), LoggingSource.UISchool);

      throw error;
    } finally {
      logger.info("...getSchoolBankAccount", LoggingSource.UISchool);
    }
  }

  async putSchoolBankAccount(
    reference: string,
    bankaccount: SchoolBankAccount,
  ): Promise<IResponseModel<SchoolBankAccount>> {
    try {
      logger.info("putSchoolBankAccount...", LoggingSource.UISchool);

      logger.debug(
        `Parameter Reference: ${reference}, School Bank Account: ${JSON.stringify(
          bankaccount,
        )}`,
        LoggingSource.UISchool,
      );

      logger.timeStart("putSchoolBankAccount call", LoggingSource.UISchool);
      const result = await this._apiClient.put<
        SchoolBankAccount,
        SchoolBankAccount
      >(this.URL + `${reference}/bankaccount`, bankaccount);

      logger.timeEnd("putSchoolBankAccount call", LoggingSource.UISchool);

      logger.debug(
        `putSchoolBankAccount response: ${JSON.stringify(result)}`,
        LoggingSource.UISchool,
      );

      return result;
    } catch (error) {
      logger.timeEnd("putSchoolBankAccount call", LoggingSource.UISchool);
      logger.error(JSON.stringify(error), LoggingSource.UISchool);

      throw error;
    } finally {
      logger.info("...putSchoolBankAccount", LoggingSource.UISchool);
    }
  }

  async update(
    reference: string,
    schoolDetail: SchoolDetail,
  ): Promise<IResponseModel<SchoolDetail>> {
    try {
      logger.info("School Update...", LoggingSource.UISchool);

      logger.debug(
        `Parameters => School: ${JSON.stringify(schoolDetail)}`,
        LoggingSource.UISchool,
      );
      logger.timeStart("school update call", LoggingSource.UISchool);
      const result = await this._apiClient.put<SchoolDetail, SchoolDetail>(
        `/school/${reference}`,
        schoolDetail,
      );

      logger.timeEnd("school update call", LoggingSource.UISchool);
      logger.debug(`School: ${JSON.stringify(result)}`, LoggingSource.UISchool);

      return result;
    } catch (error) {
      logger.timeEnd("school update call", LoggingSource.UISchool);
      logger.error(JSON.stringify(error), LoggingSource.UISchool);

      throw error;
    } finally {
      logger.info("...School Update", LoggingSource.UISchool);
    }
  }

  async updateByAdministrator(
    reference: string,
    schoolDetail: SchoolDetail,
  ): Promise<IResponseModel<SchoolDetail>> {
    try {
      logger.info("School Update By Administrator...", LoggingSource.UISchool);

      logger.debug(
        `Parameters => School: ${JSON.stringify(schoolDetail)}`,
        LoggingSource.UISchool,
      );
      logger.timeStart("school update call", LoggingSource.UISchool);
      const result = await this._apiClient.put<SchoolDetail, SchoolDetail>(
        `/school/${reference}/byadministrator`,
        schoolDetail,
      );

      logger.timeEnd("school update call", LoggingSource.UISchool);
      logger.debug(`School: ${JSON.stringify(result)}`, LoggingSource.UISchool);

      return result;
    } catch (error) {
      logger.timeEnd("school update call", LoggingSource.UISchool);
      logger.error(JSON.stringify(error), LoggingSource.UISchool);

      throw error;
    } finally {
      logger.info("...School Update By Administrator", LoggingSource.UISchool);
    }
  }

  async insert(
    schoolDetail: SchoolDetail,
  ): Promise<IResponseModel<SchoolDetail>> {
    try {
      logger.info("School Insert...", LoggingSource.UISchool);

      logger.debug(
        `Parameters => School: ${JSON.stringify(schoolDetail)}`,
        LoggingSource.UISchool,
      );
      logger.timeStart("school insert call", LoggingSource.UISchool);
      const result = await this._apiClient.post<SchoolDetail, SchoolDetail>(
        "/school",
        schoolDetail,
      );

      logger.timeEnd("school insert call", LoggingSource.UISchool);
      logger.debug(`School: ${JSON.stringify(result)}`, LoggingSource.UISchool);

      return result;
    } catch (error) {
      logger.timeEnd("school insert call", LoggingSource.UISchool);
      logger.error(JSON.stringify(error), LoggingSource.UISchool);

      throw error;
    } finally {
      logger.info("...School Insert", LoggingSource.UISchool);
    }
  }

  async getSchoolList(
    queryString?: string,
  ): Promise<IResponseModel<SchoolDetail[]>> {
    try {
      logger.info("getSchoolList...", LoggingSource.UISchool);

      logger.debug(
        `Parameters => Query String: ${queryString}`,
        LoggingSource.UISchool,
      );
      logger.timeStart("getSchoolList call", LoggingSource.UISchool);
      const result = await this._apiClient.get<SchoolDetail[]>(
        "/school" + (queryString ? `?${queryString}` : ""),
      );

      logger.timeEnd("getSchoolList call", LoggingSource.UISchool);
      logger.debug(`School: ${JSON.stringify(result)}`, LoggingSource.UISchool);

      return result;
    } catch (error) {
      logger.timeEnd("getSchoolList call", LoggingSource.UISchool);
      logger.error(JSON.stringify(error), LoggingSource.UISchool);

      throw error;
    } finally {
      logger.info("...getSchoolList", LoggingSource.UISchool);
    }
  }
}
